import React, { useState, useEffect } from "react";
import "./MicroserviceForm.css";

const MicroserviceForm = ({ 
  onSubmit, 
  initialMicroservice = null, 
  isEditing = false,
  onCancel,
  existingCategories = [] // Categories from existing microservices (e.g. from CSV imports)
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  // Combine existing categories with some defaults (only if no existing categories)
  const defaultCategories = existingCategories.length > 0 
    ? existingCategories 
    : [
        "SEO",
        "Content Marketing",
        "Social Media",
        "Email Marketing",
        "PPC",
        "Web Development",
        "Analytics",
        "Design",
        "Support",
        "Other"
      ];
  
  // All available categories (existing + option to add new)
  const allCategories = [...new Set([...defaultCategories])].sort();

  // Fill form with data of the microservice to be edited
  useEffect(() => {
    if (initialMicroservice) {
      setTitle(initialMicroservice.title || "");
      setDescription(initialMicroservice.description || "");
      
      // Check if category exists in the list or is custom
      const existingCategory = initialMicroservice.category || "";
      if (existingCategory && !allCategories.includes(existingCategory)) {
        setCategory("__custom__");
        setCustomCategory(existingCategory);
        setShowCustomCategory(true);
      } else {
        setCategory(existingCategory);
        setCustomCategory("");
        setShowCustomCategory(false);
      }
      
      setPrice(initialMicroservice.price ? initialMicroservice.price.toString() : "");
    } else {
      resetForm();
    }
  }, [initialMicroservice]);

  // Reset form
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setCustomCategory("");
    setShowCustomCategory(false);
    setPrice("");
    setError("");
  };

  // Handle category change
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__custom__") {
      setShowCustomCategory(true);
      setCategory("__custom__");
    } else {
      setShowCustomCategory(false);
      setCustomCategory("");
      setCategory(value);
    }
  };

  // Submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Basic validation - only title is required
    if (!title.trim()) {
      setError("Please enter a title for the microservice");
      return;
    }

    // If price is provided, validate it
    if (price.trim() && (isNaN(parseFloat(price)) || parseFloat(price) < 0)) {
      setError("Please enter a valid price (or leave empty)");
      return;
    }

    // Determine final category (custom or selected)
    const finalCategory = showCustomCategory && customCategory.trim() 
      ? customCategory.trim() 
      : (category === "__custom__" ? "" : category.trim());

    // Create microservice object
    const microserviceData = {
      title: title.trim(),
      description: description.trim(),
      category: finalCategory,
      price: price.trim() ? parseFloat(price) : 0, // Default to 0 if empty
      updatedAt: Date.now()
    };

    if (!isEditing) {
      microserviceData.createdAt = Date.now();
    }

    // Save microservice
    onSubmit(microserviceData);
    resetForm();
  };

  return (
    <form className="microservice-form" onSubmit={handleSubmit}>
      <div className="microservice-form__header">
        <h3 className="microservice-form__title">
          {isEditing ? "Edit Microservice" : "Create New Microservice"}
        </h3>
      </div>
      
      {error && <div className="microservice-form__error">{error}</div>}
      
      <div className="microservice-form__group">
        <label htmlFor="title" className="microservice-form__label">
          Title <span className="microservice-form__required">*</span>
        </label>
        <input
          type="text"
          id="title"
          className="microservice-form__input"
          placeholder="e.g. SEO Analysis"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      
      <div className="microservice-form__group">
        <label htmlFor="description" className="microservice-form__label">Description</label>
        <textarea
          id="description"
          className="microservice-form__textarea"
          placeholder="Detailed description of the microservice..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
        />
        <p className="microservice-form__hint">A precise description helps in selecting the appropriate microservices for an offer.</p>
      </div>
      
      <div className="microservice-form__row">
        <div className="microservice-form__group">
          <label htmlFor="category" className="microservice-form__label">Category</label>
          <select
            id="category"
            className="microservice-form__select"
            value={category}
            onChange={handleCategoryChange}
          >
            <option value="">-- Select category --</option>
            {allCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
            <option value="__custom__">➕ Add new category...</option>
          </select>
          
          {showCustomCategory && (
            <input
              type="text"
              className="microservice-form__input microservice-form__custom-category"
              placeholder="Enter new category name..."
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              autoFocus
              style={{ marginTop: '8px' }}
            />
          )}
        </div>
        
        <div className="microservice-form__group">
          <label htmlFor="price" className="microservice-form__label">
            Price (€) <span className="microservice-form__optional">(optional)</span>
          </label>
          <input
            type="number"
            id="price"
            className="microservice-form__input"
            placeholder="e.g. 49.99"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            step="0.01"
            min="0"
          />
        </div>
      </div>
      
      <div className="microservice-form__actions">
        <button 
          type="button" 
          className="microservice-form__cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="microservice-form__submit-btn"
        >
          {isEditing ? "Update" : "Create"}
        </button>
      </div>
    </form>
  );
};

export default MicroserviceForm; 